/**
 * Cart Drawer: accessible native cart drawer extending DrawerContent. Focuses the
 * role="dialog" panel on open (that announcement IS the add feedback, coordinated
 * through CartStatusAnnouncer), inerts the rest of the page because aria-modal alone
 * does not constrain browse mode, and stands down while an overlay opens above it.
 */

(function () {
	const SELECTORS = {
		PANEL: '.drawer__content',
		// Focus-restore fallback when the element that opened the drawer was re-rendered away.
		CART_TOGGLE: 'a[href$="/cart"], [data-drawer-open="DrawerCart"], [aria-label="Cart" i]',
		// Transient overlays that legitimately open ABOVE the drawer (TECH-12834).
		COMPETING_OVERLAY:
			'.klaviyo-form, .rebuy-modal, .rebuy-widget[class*="modal"], [role="dialog"], [aria-modal="true"]',
		// Utility widgets kept operable while the drawer is open. Anything added here must
		// also join the drawer's Tab cycle (getFocusableElements / isInFocusScope) or the
		// exemption paints a control the keyboard can never reach. The Gorgias launcher is
		// deliberately NOT exempt (2.4.3/2.4.7); theme.css hides it while the drawer is open.
		ISOLATION_EXEMPT: 'accessibility-toggle, [id$="drawer-accessibility"], savedby-checkout-plus',
		CHECKOUT_GROUP: '.cart-checkout-group',
		// The element that actually scrolls, and so the one the reserve is measured against.
		SCROLLER: 'cart-items',
		// The page's own content. A body child holding this is the page, not a widget wrapper.
		MAIN_CONTENT: 'main, [role="main"]',
	};

	// Most of the scrollport the pinned group may claim as scroll reserve. Past this the
	// reserve stops protecting controls and starts hiding them off the top instead.
	const MAX_RESERVE_FRACTION = 0.5;

	const LIVE_REGION_ID = 'CartStatusLiveRegion';

	// Keys this element's cart-dialog registration, so it can be withdrawn without
	// disturbing UpCart's (both are registered through the dark-launch overlap).
	const ANNOUNCER_OWNER = 'cart-drawer';

	class CartDrawer extends DrawerContent {
		constructor() {
			super();
			this.inertedElements = [];
			this.yieldedToOverlay = false;
			this.overlayObserver = null;
			this.checkoutGroupObserver = null;
			this.syncScheduled = false;
			this.unsubscribeRerenders = null;
		}

		// Setup belongs here, not in the constructor: a DOM move disconnects and reconnects
		// the same element, and disconnectedCallback tears both of these down. Idempotent.
		connectedCallback() {
			// super first: the base binds the opener and closer buttons here, and they live outside this
			// element, so skipping it leaves the header cart button wired to nothing.
			super.connectedCallback();
			this.registerWithAnnouncer();
			this.subscribeToRerenders();
		}

		registerWithAnnouncer() {
			const announcer = window.UFT?.CartStatusAnnouncer;
			if (!announcer?.registerCartDialog) {
				window.UFT?.FailureReporter?.report?.(
					'cart-drawer-no-announcer',
					'CartDrawer: CartStatusAnnouncer unavailable; add-to-cart announcements will not coordinate with the drawer.',
					{ level: 'warning' }
				);
				return;
			}
			announcer.registerCartDialog(ANNOUNCER_OWNER);
		}

		subscribeToRerenders() {
			const eventManager = window.UFT?.EventManager;
			if (!eventManager?.on) {
				window.UFT?.FailureReporter?.report?.(
					'cart-drawer-no-event-manager',
					'CartDrawer: UFT.EventManager unavailable; focus recovery after cart re-renders is disabled.',
					{ level: 'warning' }
				);
				return;
			}
			// Keep the unsubscribe handle: a theme-editor section re-render constructs a fresh
			// instance, and without cleanup every old one stays retained and keeps firing.
			this.unsubscribeRerenders = eventManager.on(eventManager.eventStrings.CART_RERENDERED, () => {
				this.recoverFocusAfterRerender();
				// The rerender replaced the node the observer was watching.
				this.startCheckoutGroupObserver();
			});
		}

		getPanel() {
			const panel = this.querySelector(SELECTORS.PANEL);
			if (!panel) {
				window.UFT?.FailureReporter?.report?.(
					'cart-drawer-no-panel',
					'CartDrawer: .drawer__content panel missing; dialog focus and isolation cannot apply.'
				);
			}
			return panel;
		}

		open(event) {
			const wasOpen = this.classList.contains(this.activeClass);
			const focusWasInside = this.contains(document.activeElement);
			const announcer = window.UFT?.CartStatusAnnouncer;
			announcer?.suppressPendingAnnouncement?.();
			super.open(event);
			this.startOverlayObserver();
			this.startCheckoutGroupObserver();
			this.syncPageIsolation();
			// Neither an already-open drawer nor focus already inside produces a new
			// dialog-focus announcement, so hand the add feedback back to the live region.
			if (focusWasInside || wasOpen) {
				announcer?.releasePendingAnnouncement?.();
			}
		}

		// Any path that does NOT announce must hand the suppression back, or the add is
		// silent to assistive tech and the retained message is stranded (WCAG 4.1.3).
		focusOnOpen() {
			if (!this.seatDialogFocus()) {
				window.UFT?.CartStatusAnnouncer?.releasePendingAnnouncement?.();
			}
		}

		// The drawer closed inside its own open transition, so focusOnOpen never ran and the
		// suppression above was never handed back. Same obligation, reached a different way.
		//
		// Not done in close(): on the ordinary path the dialog's own announcement stands in for
		// the add, and releasePendingAnnouncement REPLAYS whatever it still holds. Nothing
		// clears suppressedMessage on that path, so releasing from close() would announce a
		// stale add every time the drawer shut.
		cancelFocusOnOpen() {
			window.UFT?.CartStatusAnnouncer?.releasePendingAnnouncement?.();
		}

		// Focus the panel itself, not the close button, so screen readers announce the
		// dialog role and label once. The return value answers "was the add announced?".
		seatDialogFocus() {
			if (this.yieldedToOverlay || this.contains(document.activeElement)) {
				return false;
			}
			const panel = this.getPanel();
			if (!panel) {
				return false;
			}
			panel.focus();
			// The panel can refuse focus (inert ancestor, display juggling mid transition).
			return document.activeElement === panel;
		}

		close() {
			if (!this.classList.contains(this.activeClass)) {
				return;
			}
			this.stopOverlayObserver();
			this.stopCheckoutGroupObserver();
			this.restorePage();
			this.yieldedToOverlay = false;
			super.close();
		}

		disconnectedCallback() {
			// super first: the base treats a disconnect as a close, which is what releases
			// the scroll-lock refcount and the open stack.
			super.disconnectedCallback();
			this.stopOverlayObserver();
			this.stopCheckoutGroupObserver();
			this.restorePage();
			this.unsubscribeRerenders?.();
			this.unsubscribeRerenders = null;
			// Or the announcer keeps deferring every add to a dialog that no longer exists.
			window.UFT?.CartStatusAnnouncer?.unregisterCartDialog?.(ANNOUNCER_OWNER);
		}

		// While a competing overlay is up it owns the keyboard: its Escape must close it,
		// not the drawer underneath.
		handleEscape(event) {
			if (this.yieldedToOverlay) {
				return;
			}
			super.handleEscape(event);
		}

		checkFocus(event) {
			if (!this.ownsKeyboard()) {
				return;
			}
			super.checkFocus(event);
		}

		// A competing overlay (TECH-12834) or another drawer stacked on top takes the keyboard
		// away. The stacked drawer carries no role="dialog", so topmost is the only honest test.
		ownsKeyboard() {
			return !this.yieldedToOverlay && this.isTopmostDrawer();
		}

		// The drawer PLUS the widgets isolatePage left operable, drawer first. The two groups
		// stay separate deliberately (trapTabKey forces the hop between them, see
		// wrapForwardTab), and each is ordered internally by the two methods below, so this
		// offers checkFocus the same order the Tab trap uses rather than a second opinion.
		getFocusableElements() {
			return [...this.drawerFocusables(), ...this.exemptFocusables()];
		}

		// Stable, so controls sharing an ordering node (siblings inside one shadow root, which
		// compare as equal) keep the order their own root gave them.
		inDocumentOrder(elements) {
			return elements.sort((first, second) => {
				const relation = this.orderingNode(first).compareDocumentPosition(this.orderingNode(second));
				if (relation & Node.DOCUMENT_POSITION_FOLLOWING) {
					return -1;
				}
				if (relation & Node.DOCUMENT_POSITION_PRECEDING) {
					return 1;
				}
				return 0;
			});
		}

		// A shadow-rendered control has no position in the document to compare, so it sorts
		// where its host sits, which is where Tab reaches it from.
		orderingNode(element) {
			let node = element;
			while (node?.getRootNode?.()?.host) {
				node = node.getRootNode().host;
			}
			return node;
		}

		// The drawer's own controls, including any that FOCUSABLE_SELECTOR cannot see, in
		// document order. Ordered HERE rather than in getFocusableElements, because that
		// method is read only by the base checkFocus, which cart-drawer's own handleKeydown
		// comment notes cannot fire once isolatePage has inerted the page. trapTabKey is the
		// wrap that runs, and it reads these two methods directly.
		//
		// widgetFocusables appends, so unsorted, a control a widget renders into a shadow root
		// lands after every light-DOM control however far up the panel its host actually sits.
		// savedby-checkout-plus mounts inside .cart-summary, above the checkout button, so the
		// moment it exposes a focusable control the raw order hands "last" to it. wrapForwardTab
		// only preventDefaults at "last", so a Tab off the checkout button would fall through to
		// a page isolatePage has made inert, taking focus out of the dialog for browser chrome
		// (2.1.2). It exposes none today, which is why this is a latent fix rather than a
		// reported break.
		drawerFocusables() {
			return this.inDocumentOrder([...super.getFocusableElements(), ...this.widgetFocusables(this)]);
		}

		// Ordered for the same reason, across roots: exemptIsolationRoots walks body children in
		// tree order, but a root's own shadow-rendered controls would otherwise trail its
		// light-DOM ones.
		exemptFocusables() {
			return this.inDocumentOrder(
				this.exemptIsolationRoots().flatMap((root) => [
					...super.getFocusableElements(root),
					...this.widgetFocusables(root),
				])
			);
		}

		// Controls FOCUSABLE_SELECTOR is blind to. An ISOLATION_EXEMPT widget can render its control
		// into a shadow root (savedby-checkout-plus does, which is why the pre-TECH-12871 checkFocus
		// selector named the element rather than anything inside it), and the wrap would then
		// preventDefault at the last light-DOM control, blocking the browser's own navigation into that
		// shadow content (WCAG 2.1.1). A closed root leaves only the host, and only when it is really
		// focusable: a wrap endpoint that .focus() no-ops on parks focus nowhere.
		widgetFocusables(root) {
			const hosts = [...root.querySelectorAll(SELECTORS.ISOLATION_EXEMPT)];
			if (typeof root.matches === 'function' && root.matches(SELECTORS.ISOLATION_EXEMPT)) {
				hosts.unshift(root);
			}
			return hosts.flatMap((host) => {
				if (host.shadowRoot) {
					return super.getFocusableElements(host.shadowRoot);
				}
				return host.tabIndex >= 0 ? [host] : [];
			});
		}

		// Focus legitimately sits outside the drawer element when it is on one of those widgets,
		// so the Tab wrap must not read that as an escape. Node.contains does not cross shadow
		// boundaries, so a control inside a widget's shadow root is tested through its host,
		// which is also what document.activeElement reports.
		isInFocusScope(element) {
			for (let node = element; node; node = node.getRootNode()?.host) {
				if (super.isInFocusScope(node) || this.exemptIsolationRoots().some((root) => root.contains(node))) {
					return true;
				}
			}
			return false;
		}

		// Shared with isolatePage so the inert set and the focus cycle can never disagree.
		// Matched on the child OR its subtree: apps portal their widget into a wrapper div.
		exemptIsolationRoots() {
			const roots = [];
			for (const child of document.body.children) {
				if (child === this || child.contains(this) || typeof child.matches !== 'function') {
					continue;
				}
				// The subtree match below exists because apps portal their widget into a wrapper
				// div, but a wrapper is all it may exempt. On /cart, SavedBy renders inside the
				// PAGE's own cart summary, so <main> matched it and the entire page stayed out of
				// inert: measured 13 focusables live behind an open dialog, with Tab walking
				// straight into them (2.1.2, QA's ADA-8). It reproduced only on /cart because that
				// is the only surface where SavedBy sits inside main. Nothing is lost by inerting
				// the page copy, since the drawer renders its own SavedBy instance.
				if (child.matches(SELECTORS.MAIN_CONTENT) || child.querySelector(SELECTORS.MAIN_CONTENT)) {
					continue;
				}
				if (child.matches(SELECTORS.ISOLATION_EXEMPT) || child.querySelector(SELECTORS.ISOLATION_EXEMPT)) {
					roots.push(child);
				}
			}
			return roots;
		}

		// The base focusin wrap can't fire when the whole page is inert and nothing follows the
		// drawer in the tab order, so Tab off the last control would escape to browser chrome.
		handleKeydown(event) {
			if (event.key === 'Tab' && this.classList.contains(this.activeClass) && this.ownsKeyboard()) {
				this.trapTabKey(event);
			}
			super.handleKeydown(event);
		}

		// Both lists are computed ONCE here and passed down, so the wrap methods can't disagree.
		trapTabKey(event) {
			const own = this.drawerFocusables();
			const exempt = this.exemptFocusables();
			if (own.length + exempt.length === 0) {
				return;
			}
			const active = this.deepActiveElement();
			if (event.shiftKey) {
				this.wrapBackwardTab(event, own, exempt, active);
				return;
			}
			this.wrapForwardTab(event, own, exempt, active);
		}

		// Exempt widgets can sit BEFORE the drawer in DOM order, so both wrap methods force the
		// hop explicitly: drawer controls -> exempt widgets -> back to the drawer's first.
		wrapForwardTab(event, own, exempt, active) {
			const last = exempt[exempt.length - 1] || own[own.length - 1];
			if (active === last) {
				event.preventDefault();
				(own[0] || exempt[0]).focus();
				return;
			}
			if (exempt.length > 0 && active === own[own.length - 1]) {
				event.preventDefault();
				exempt[0].focus();
			}
		}

		wrapBackwardTab(event, own, exempt, active) {
			const ownLast = own[own.length - 1];
			// From the panel (the landing spot on open), wrap to the drawer's own last control:
			// Shift-Tab from "Cart, dialog" should reach Checkout, not the page widgets.
			if (active === this.getPanel()) {
				event.preventDefault();
				(ownLast || exempt[exempt.length - 1])?.focus();
				return;
			}
			if (active === (own[0] || exempt[0]) || !this.isInFocusScope(active)) {
				event.preventDefault();
				(exempt[exempt.length - 1] || ownLast)?.focus();
				return;
			}
			if (exempt.length > 0 && active === exempt[0]) {
				event.preventDefault();
				ownLast?.focus();
			}
		}

		// Restore chain: trigger, visible cart toggle, #main landmark. Each step is verified
		// because .focus() is a silent no-op on an unfocusable element.
		restoreFocusToOpener(openButton) {
			const anotherDrawerIsOpen = document.querySelector(`.${this.activeClass}`) !== null;
			if (anotherDrawerIsOpen) {
				return;
			}
			if (this.tryFocus(openButton)) {
				return;
			}
			if (this.tryFocus(this.getCartToggle())) {
				return;
			}
			focusFallbackLandmark();
		}

		tryFocus(element) {
			if (!element || typeof element.focus !== 'function' || !element.isConnected) {
				return false;
			}
			element.focus();
			return document.activeElement === element;
		}

		getCartToggle() {
			const toggles = [...document.querySelectorAll(SELECTORS.CART_TOGGLE)];
			return toggles.find((element) => element.getClientRects().length > 0) || toggles[0] || null;
		}

		// Cart mutations innerHTML-swap the drawer body, dropping focus to <body>, which page
		// isolation makes unreachable. Guarded on ownsKeyboard so a drawer stacked above keeps
		// its focus, and on isInFocusScope so an exempt widget counts as still seated.
		recoverFocusAfterRerender() {
			if (!this.classList.contains(this.activeClass) || !this.ownsKeyboard()) {
				return;
			}
			if (this.isInFocusScope(document.activeElement)) {
				return;
			}
			const panel = this.getPanel();
			if (!panel) {
				return;
			}
			// The panel, not the close button. Seating a real control here looked better for the
			// keyboard case, but Safari and Firefox do not focus on click, so after any mouse-driven
			// cart change activeElement is <body> and this path runs: focus landed on Close and the
			// next Space, which a shopper presses to scroll, dismissed the drawer.
			//
			// The keyboard case is answered with a ring instead, scoped to this recovery. The panel
			// is otherwise deliberately ringless (see cart-drawer.css): on open there is nothing for
			// a sighted user to have lost, because the dialog role and label are announced. Here
			// there is, because the control they just pressed has gone. :focus-visible carries the
			// distinction for free, since it does not match after a click, so a mouse user still
			// sees nothing.
			panel.classList.add('drawer__content--focus-recovered');
			panel.focus();
			// Two conditions before the cleanup is armed, and the second is the load-bearing one.
			//
			// focus() is a silent no-op on a panel that refuses it (inert ancestor, or display
			// juggling mid transition, as seatDialogFocus notes), and the blur that removes the
			// modifier would then never come.
			//
			// :focus-visible is then checked HERE, at arm time, rather than being left to the CSS.
			// Leaving it to the CSS was wrong: :focus-visible is a live UA heuristic, not a record of
			// how focus arrived, and per Selectors 4 a keypress makes the currently focused element
			// start matching it. Since the mouse path is the common one here (Safari and Firefox do
			// not focus on click, so activeElement is <body> after any mouse-driven change), the
			// modifier would sit armed on the panel until something blurred it, and the shopper's
			// next Escape or arrow key would paint the full-height ring on a mutation they had
			// already moved on from. Testing it now means a mouse recovery never carries the class at
			// all, and the ring only ever belongs to a keyboard user who just lost their control.
			if (document.activeElement !== panel || !panel.matches(':focus-visible')) {
				panel.classList.remove('drawer__content--focus-recovered');
				return;
			}
			panel.addEventListener('blur', () => panel.classList.remove('drawer__content--focus-recovered'), {
				once: true,
			});
		}

		startOverlayObserver() {
			if (this.overlayObserver) {
				return;
			}
			this.overlayObserver = new MutationObserver(() => {
				this.scheduleIsolationSync();
			});
			this.overlayObserver.observe(document.body, {
				childList: true,
				subtree: true,
				attributes: true,
				attributeFilter: ['class', 'style'],
			});
		}

		stopOverlayObserver() {
			if (this.overlayObserver) {
				this.overlayObserver.disconnect();
				this.overlayObserver = null;
			}
			this.syncScheduled = false;
		}

		// Publishes the pinned checkout group's height so the scroller reserves room for it
		// (theme.css: cart-items { scroll-padding-bottom }), for WCAG 2.2 SC 2.4.11. Measured
		// because it varies with the merchant's blocks and with a discount row appearing.
		startCheckoutGroupObserver() {
			if (typeof ResizeObserver === 'undefined') {
				return;
			}
			const group = this.querySelector(SELECTORS.CHECKOUT_GROUP);
			this.checkoutGroupObserver?.disconnect();
			this.checkoutGroupObserver = null;
			if (!group) {
				// An empty cart pins nothing, so clear any height left from a populated one.
				this.style.removeProperty('--cart-checkout-group-h');
				return;
			}
			// Capped at half the scrollport. scroll-padding-bottom larger than the scrollport
			// inverts the criterion it exists for: the browser scrolls the target above the
			// reserve, and once the reserve is the whole scrollport that lands it off the TOP.
			// Measured on a 390px-tall viewport (any phone in landscape), where the group is
			// 325px against a 323px scroller: the first stepper landed 19px above the
			// scrollport, invisible, and zeroing the reserve put it back at 243px. Over-
			// reserving is only the safer direction while the reserve still fits.
			const publish = () => {
				const measured = Math.ceil(group.getBoundingClientRect().height);
				const scroller = group.closest(SELECTORS.SCROLLER);
				const room = scroller ? Math.floor(scroller.clientHeight * MAX_RESERVE_FRACTION) : measured;
				this.style.setProperty('--cart-checkout-group-h', `${Math.min(measured, room)}px`);
			};
			publish();
			this.checkoutGroupObserver = new ResizeObserver(publish);
			this.checkoutGroupObserver.observe(group);
		}

		stopCheckoutGroupObserver() {
			this.checkoutGroupObserver?.disconnect();
			this.checkoutGroupObserver = null;
		}

		scheduleIsolationSync() {
			if (this.syncScheduled) {
				return;
			}
			this.syncScheduled = true;
			requestAnimationFrame(() => {
				this.syncScheduled = false;
				this.syncPageIsolation();
			});
		}

		// The drawer is modal against the page, not against other overlays. Every isolation
		// decision funnels through here.
		syncPageIsolation() {
			if (!this.classList.contains(this.activeClass)) {
				return;
			}

			const overlay = this.findCompetingOverlay();
			if (overlay) {
				if (!this.yieldedToOverlay) {
					this.yieldedToOverlay = true;
					this.restorePage();
				}
				return;
			}

			if (this.yieldedToOverlay) {
				this.yieldedToOverlay = false;
				this.isolatePage();
				// The overlay took focus with it when it closed.
				this.recoverFocusAfterRerender();
				return;
			}

			this.isolatePage();
		}

		// aria-modal isn't enough for browse-mode screen readers. Idempotent.
		isolatePage() {
			const liveRegion = document.getElementById(LIVE_REGION_ID);
			const exemptRoots = this.exemptIsolationRoots();

			for (const child of document.body.children) {
				if (child.inert) {
					continue;
				}
				if (child === this || child.contains(this)) {
					continue;
				}
				if (liveRegion && (child === liveRegion || child.contains(liveRegion))) {
					continue;
				}
				if (exemptRoots.includes(child)) {
					continue;
				}
				child.inert = true;
				this.inertedElements.push(child);
			}
		}

		restorePage() {
			this.inertedElements.forEach((element) => {
				element.inert = false;
			});
			this.inertedElements = [];
		}

		findCompetingOverlay() {
			const liveRegion = document.getElementById(LIVE_REGION_ID);

			for (const child of document.body.children) {
				if (child === this || child.contains(this)) {
					continue;
				}
				if (liveRegion && (child === liveRegion || child.contains(liveRegion))) {
					continue;
				}
				if (typeof child.matches !== 'function') {
					continue;
				}

				const candidates = child.matches(SELECTORS.COMPETING_OVERLAY)
					? [child]
					: [...child.querySelectorAll(SELECTORS.COMPETING_OVERLAY), ...this.shadowOverlays(child)];
				if (candidates.some((element) => this.isOverlayVisible(element) && this.isInFixedLayer(element, child))) {
					return child;
				}
			}
			return null;
		}

		// An app can portal its overlay into an open shadow root, invisible to the light DOM: UpCart
		// renders its role="dialog" panel inside #upCart's (see 3p-upcart-accessibility.js). Without
		// this the drawer never yields during the dark-launch overlap and isolatePage() inerts #upCart,
		// freezing it. Only the body child's own root, since this runs on every mutation-observer tick.
		shadowOverlays(child) {
			if (!child.shadowRoot) {
				return [];
			}
			return [...child.shadowRoot.querySelectorAll(SELECTORS.COMPETING_OVERLAY)];
		}

		// Overlays render in a fixed-position layer; in-page content that merely matches an
		// overlay hook (a Klaviyo newsletter EMBED in the footer) does not. The walk crosses shadow
		// boundaries (parentElement is null at a shadow root, so step to its host) or a shadow-rooted
		// overlay could never reach root and would always read as in-page.
		isInFixedLayer(element, root) {
			for (let node = element; node; node = node.parentElement || node.getRootNode()?.host) {
				if (window.getComputedStyle(node).position === 'fixed') {
					return true;
				}
				if (node === root) {
					break;
				}
			}
			return false;
		}

		// Several apps park sized dialog nodes offscreen or at opacity:0 while "closed"
		// (Sezzle); treating one as open would permanently disable isolation.
		isOverlayVisible(element) {
			const rect = element.getBoundingClientRect();
			if (rect.width === 0 || rect.height === 0) {
				return false;
			}
			const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
			const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
			if (rect.bottom <= 0 || rect.right <= 0 || rect.top >= viewportHeight || rect.left >= viewportWidth) {
				return false;
			}
			if (typeof element.checkVisibility === 'function') {
				return element.checkVisibility({
					visibilityProperty: true,
					contentVisibilityAuto: true,
					opacityProperty: true,
				});
			}
			return true;
		}
	}

	if (!customElements.get('cart-drawer')) {
		customElements.define('cart-drawer', CartDrawer);
	}
})();
